import React, { useState } from 'react';
import { Modal, Select, Tabs } from 'antd';
import { fetchApiAuth } from '../../../../../../core/utils/api';
import dayjs from 'dayjs';
import DvrHealthReportAlarmLog from '../../components/DvrHealthReportAlarmLog';
import DvrHealthReportScreenshot from '../../components/DvrHealthReportScreenshot';
import { CheckmarkFilled, CloseFilled, WarningAltFilled } from '@carbon/icons-react';
import { DvrHealthStatus, IcarCommonAlarmType } from '../../../../../../types/types';

import './ExpandedDvrHealthReportModal.scss';
import RegistrationPlate from '../../../../../../components/RegistrationPlate/registrationplate';

interface DvrHealthReportResponse {
    reports: any[];
    asset: { [key: string]: any };
}

interface ExpandedDvrHealthReportModalProps {
    isModalOpen: boolean;
    selectedAssetId: number | null;
    selectedDate: string | null;
    selectedStatus: DvrHealthStatus | null;
    closeModal: () => void;
}

const ExpandedDvrHealthReportModal: React.FC<ExpandedDvrHealthReportModalProps> = ({
    isModalOpen = false,
    selectedAssetId = null,
    selectedDate = null,
    selectedStatus = null,
    closeModal = () => { },
}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [data, setData] = useState<any[]>([]);
    const [asset, setAsset] = useState<any>(null);
    const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

    let abortController = new AbortController();
    const cleanUp = () => {
        setLoading(true); // so it always opens with the loading state
        setAsset(null);
        abortController.abort();
    }
    const getData = async (): Promise<DvrHealthReportResponse> => {
        setLoading(true);
        const url = `report/get-dvr-health-report/asset/${selectedAssetId}?date=${selectedDate}`;
        abortController = new AbortController();
        return fetchApiAuth({
            method: 'GET',
            url: url,
            signal: abortController.signal,
        })
            .then((data) => {
                return data?.data ?? [];
            })
            .catch(() => [])
            .finally(() => {
                setLoading(false);
            });
    }
    const openedStateChanged = async (open) => {
        if (open) {
            const responseData = await getData();
            const data = responseData?.reports ?? [];
            setAsset(responseData?.asset);
            setData(data);
            if (data?.length) {
                let firstReportId = data[0].id;
                let firstWarning;
                if (selectedStatus) {
                    if (selectedStatus === DvrHealthStatus.Critical) {
                        firstWarning = data.find(d => d.has_errors); // currently only supported ID, adapt later
                    } else if (selectedStatus === DvrHealthStatus.Warning) {
                        firstWarning = data.find(d => d.has_alarms); // currently only supported ID, adapt later
                    }

                    if (firstWarning !== undefined) {
                        firstReportId = firstWarning.id;
                    }
                }
                setSelectedReportId(firstReportId);
            }
        } else {
            cleanUp();
        }
    }
    const renderSelectedTime = () => {
        if (selectedReportId === undefined || selectedReportId === null) {
            return null;
        } else {
            const selectedReport = data.filter(d => d.id === selectedReportId)[0];
            return (
                <>
                    <DvrHealthReportAlarmLog alarmLogs={selectedReport?.dvrHealthStatements} />
                    <DvrHealthReportScreenshot screenshots={selectedReport?.dvrHealthReportScreenshot} />
                </>
            );
        }
    }

    return (

        <Modal
            title={`DVR Health Check - ${dayjs(selectedDate).format('ddd Do MMMM')}`}
            open={isModalOpen}
            onCancel={closeModal}
            afterClose={cleanUp}
            footer={null}
            width={600}
            loading={loading}
            afterOpenChange={openedStateChanged}
            className='feedback-modal'
        >
            <div className='asset-info'>
                <div className='asset-name'>
                    {asset?.name}
                </div>
                <RegistrationPlate reg={asset?.registration} />
            </div>
            <div className='main-icon mt-2'>
                {data?.length && (
                    <>
                        <Select
                            allowClear={false}
                            placeholder="Select Time"
                            onChange={val => setSelectedReportId(val)}
                            value={selectedReportId}
                            style={{
                                width: 160
                            }}
                        >
                            {data.map(d => (
                                <Select.Option
                                    key={d.id}
                                    value={d.id}
                                >

                                    {d.has_errors ? (
                                        <CloseFilled
                                            size={16}
                                            fill='#B81D13'
                                            style={{ verticalAlign: 'middle' }}
                                        />
                                    ) : (d.has_alarms ? (
                                        <WarningAltFilled
                                            size={16}
                                            fill='#EFB700'
                                            style={{ verticalAlign: 'middle' }}
                                        />
                                    ) : (
                                        <CheckmarkFilled
                                            size={16}
                                            fill='#008450'
                                            style={{ verticalAlign: 'middle' }}
                                        />
                                    ))}
                                    &ensp;&ensp;
                                    {d.reported_at.split(' ')[1]}
                                </Select.Option>
                            ))}
                        </Select>
                        {renderSelectedTime()}
                    </>
                )}
            </div>
        </Modal>
    );
}

export default ExpandedDvrHealthReportModal;